import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Input, Button, Breadcrumb, Radio, Modal, message } from "antd";
import { RightOutlined, EditOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PostUpdateData from "../../../hooks/postUpdate";
import { ReactComponent as Tick } from "../../../assets/Icons/tick.svg";
import { ReactComponent as UploadIcon } from "../../../assets/Icons/upload.svg";
import CertificatePreview, {
  ALIGNMENT_OPTIONS,
  DEFAULT_ALIGNMENT,
  normalizeAlignment,
} from "./CertificatePreview";

const tokens = {
  navy: "#1F243B",
  slate: "#63698A",
  placeholder: "#9E9E9E",
  borderLight: "#EBEBEB",
  dashed: "#A5A8B5",
  link: "#4C91D9",
  green: "#0BAE83",
  addBg: "#E0FCF2",
  addTextDisabled: "#8F919D",
  addEnabledBg: "#C2FAE5",
};

const ACCEPTED_TYPES = ["image/png", "image/jpeg"];
const MAX_FILE_MB = 5;

const PageWrapper = styled.div`
  padding: 24px 32px 32px;
  background: #ffffff;
  min-height: 100%;
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  font-size: 15px;
  margin-bottom: 24px;

  .ant-breadcrumb-link {
    color: ${tokens.slate};
  }

  .current {
    color: ${tokens.navy};
    font-weight: 500;
  }
`;

const FieldBlock = styled.div`
  margin-bottom: 28px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 15px;
  color: ${tokens.slate};
  margin-bottom: 10px;
`;

const StyledInput = styled(Input)`
  height: 44px;
  border-radius: 10px;
  border: 1px solid ${tokens.borderLight};
  font-size: 15px;
  color: ${tokens.navy};

  &::placeholder {
    color: ${tokens.placeholder};
  }

  &:hover,
  &:focus,
  &.ant-input-focused {
    border-color: ${tokens.borderLight};
    box-shadow: none;
  }
`;

/* Empty state: Figma shows only this dashed drop area until an image is
   uploaded - the preview and Text Alignment panel appear after. */
const DropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 300px;
  border: 1px dashed ${(props) => (props.$dragging ? tokens.green : tokens.dashed)};
  border-radius: 8px;
  background: ${(props) => (props.$dragging ? "#F3FDF9" : "#ffffff")};
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease;

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 8px;
  }
`;

const DropTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${tokens.navy};
`;

const DropHint = styled.div`
  font-size: 15px;
  color: ${tokens.slate};
`;

const LinkButton = styled.button`
  border: none;
  background: transparent;
  color: ${tokens.link};
  font-size: 15px;
  padding: 0;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

const CertificateSection = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.65fr);
  border: 1px solid ${tokens.borderLight};
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CertificatePreviewArea = styled.div`
  background: #c7d4e7;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
`;

const CanvasWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 850px;
  box-shadow: 0 1px 4px rgba(31, 36, 59, 0.12);
`;

/* Figma: hovering the preview dims it and shows the change-background card.
   :focus-within keeps it reachable by keyboard too. */
const ChangeBackgroundOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.$dragging ? "rgba(11, 174, 131, 0.25)" : "rgba(99, 105, 128, 0.55)")};
  opacity: ${(props) => (props.$dragging ? 1 : 0)};
  transition: opacity 0.2s ease;

  ${CanvasWrapper}:hover &,
  ${CanvasWrapper}:focus-within & {
    opacity: 1;
  }
`;

const ChangeBackgroundCard = styled.div`
  width: 290px;
  background: #ffffff;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  text-align: center;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.2);
`;

const ChangeBackgroundIcon = styled.div`
  color: #9c9c9c;
  font-size: 24px;
  margin-bottom: 10px;
`;

const ChangeBackgroundText = styled.div`
  font-size: 16px;
  color: ${tokens.slate};
  margin-bottom: 6px;
`;

const AlignmentPanel = styled.div`
  background: #ffffff;
  padding: 28px 24px;
  border-left: 1px solid ${tokens.borderLight};
  overflow-y: auto;
`;

const AlignmentTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${tokens.navy};
  margin-bottom: 24px;
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: 18px;

  .ant-radio-wrapper {
    margin: 0;
    color: ${tokens.slate};
    font-size: 15px;
    line-height: 1.3;
  }

  .ant-radio-inner {
    width: 18px;
    height: 18px;
  }

  .ant-radio-wrapper:hover .ant-radio-inner,
  .ant-radio-checked .ant-radio-inner {
    border-color: ${tokens.green};
  }

  .ant-radio-checked .ant-radio-inner {
    background-color: ${tokens.green};
  }

  .ant-radio-checked .ant-radio-inner::after {
    background-color: #ffffff;
  }
`;

const FooterBar = styled(Row)`
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid ${tokens.borderLight};
`;

const CancelButton = styled(Button)`
  height: 42px;
  min-width: 130px;
  border-radius: 10px;
  border: 1px solid ${tokens.navy};
  background: #ffffff;
  color: ${tokens.navy};
  font-size: 15px;

  &:hover,
  &:focus {
    border-color: ${tokens.navy} !important;
    background: #ffffff !important;
    color: ${tokens.navy} !important;
  }
`;

const AddButton = styled(Button)`
  height: 42px;
  min-width: 130px;
  border-radius: 10px;
  border: none;
  font-size: 15px;
  margin-left: 16px;
  background: ${tokens.addEnabledBg};
  color: ${tokens.navy};

  &:hover,
  &:focus {
    background: ${tokens.addEnabledBg} !important;
    color: ${tokens.navy} !important;
  }

  &:disabled,
  &:disabled:hover {
    background: ${tokens.addBg} !important;
    color: ${tokens.addTextDisabled} !important;
  }
`;

const validateImage = (file) => {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return "Please upload a PNG or JPG image.";
  }
  if (file.size > MAX_FILE_MB * 1024 * 1024) {
    return `Image must be smaller than ${MAX_FILE_MB}MB.`;
  }
  return null;
};

export default function AddingCertificateForm({ onCancel, onSubmit, certificate: initialCertificate }) {
  const isEditMode = !!initialCertificate;
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.authTracking.userInfo);

  const [name, setName] = useState(initialCertificate?.certificate_name || "");
  const [typeOfCertificate, setTypeOfCertificate] = useState(initialCertificate?.certificate_type || "");
  const [previewSrc, setPreviewSrc] = useState(initialCertificate?.background_image || null);
  const [file, setFile] = useState(null);
  const [selectedAlignment, setSelectedAlignment] = useState(
    initialCertificate ? normalizeAlignment(initialCertificate.text_alignment) : DEFAULT_ALIGNMENT
  );
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);

  const fileInputRef = useRef(null);
  const objectUrlRef = useRef(null);
  const redirectTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      clearTimeout(redirectTimerRef.current);
    },
    []
  );

  const goBackToCertificates = () => navigate("/settings-course", { state: { activeKey: "2" } });

  // Figma keeps Add disabled until a background image has been uploaded.
  const canSubmit = name.trim().length > 0 && !!previewSrc && !submitting;

  const selectFile = (selected) => {
    if (!selected) return;
    const error = validateImage(selected);
    if (error) {
      message.error(error);
      return;
    }
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = URL.createObjectURL(selected);
    setFile(selected);
    setPreviewSrc(objectUrlRef.current);
  };

  const openFilePicker = (event) => {
    event?.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleNativeFileChange = (event) => {
    selectFile(event.target.files?.[0]);
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
  };

  const dragHandlers = {
    onDragOver: (event) => {
      event.preventDefault();
      setDragging(true);
    },
    onDragLeave: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
    },
    onDrop: (event) => {
      event.preventDefault();
      setDragging(false);
      selectFile(event.dataTransfer.files?.[0]);
    },
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!userInfo?.clientId) {
      message.error("You must be logged in to save a certificate");
      return;
    }

    setSubmitting(true);
    try {
      // Keeps the existing image in edit mode unless a new file was picked.
      let backgroundImageUrl = file ? null : previewSrc;

      if (file) {
        const urlResponse = await PostUpdateData({
          endPoint: `/course/getuploadurl/${userInfo.clientId}`,
          method: "POST",
          obj: { file_name: file.name, content_type: file.type },
        });
        const { uploadUrl, fileUrl } = urlResponse?.response || {};
        if (!uploadUrl || !fileUrl) {
          throw new Error("Could not get an upload URL");
        }
        const uploadResult = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!uploadResult.ok) {
          throw new Error(`S3 upload failed with status ${uploadResult.status}`);
        }
        backgroundImageUrl = fileUrl;
      }

      const response = await PostUpdateData({
        endPoint: isEditMode
          ? `/settings/updatecertificate/${userInfo.clientId}/${initialCertificate.certificate_id}`
          : `/settings/addcertificate/${userInfo.clientId}`,
        method: isEditMode ? "PATCH" : "POST",
        obj: {
          certificate_name: name.trim(),
          certificate_type: typeOfCertificate.trim(),
          background_image: backgroundImageUrl,
          text_alignment: selectedAlignment,
        },
      });

      const succeeded = isEditMode
        ? response?.statusCode === 200 || response?.status === 200
        : response?.statusCode === 201;

      if (!succeeded) {
        message.error("Something went wrong while saving the certificate");
        setSubmitting(false);
        return;
      }

      onSubmit?.(response.response?.[0] ?? response.response);
      // Figma: centred "Added" confirmation, then back to the list.
      setSuccessVisible(true);
      redirectTimerRef.current = setTimeout(goBackToCertificates, 1500);
    } catch (error) {
      console.error("Error saving certificate:", error);
      message.error(
        error?.response?.data?.message || "Something went wrong while saving the certificate"
      );
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <StyledBreadcrumb separator={<RightOutlined style={{ fontSize: 11 }} />}>
        <Breadcrumb.Item onClick={goBackToCertificates} style={{ cursor: "pointer" }}>
          Certificate
        </Breadcrumb.Item>
        <Breadcrumb.Item className="current">
          {isEditMode ? "Editing Certificate" : "Adding Certificate"}
        </Breadcrumb.Item>
      </StyledBreadcrumb>

      <Row gutter={[32, 0]}>
        <Col xs={24} md={12}>
          <FieldBlock>
            <FieldLabel htmlFor="certificate-name">Name</FieldLabel>
            <StyledInput
              id="certificate-name"
              placeholder="Eg. Classic"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </FieldBlock>
        </Col>
        <Col xs={24} md={12}>
          <FieldBlock>
            <FieldLabel htmlFor="certificate-type">Type of Certificate</FieldLabel>
            <StyledInput
              id="certificate-type"
              placeholder="Eg. Certificate of Completion"
              value={typeOfCertificate}
              onChange={(event) => setTypeOfCertificate(event.target.value)}
            />
          </FieldBlock>
        </Col>
      </Row>

      <FieldLabel>Upload certificate background image</FieldLabel>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={handleNativeFileChange}
      />

      {!previewSrc ? (
        <DropZone $dragging={dragging} {...dragHandlers}>
          <UploadIcon />
          <DropTitle>Drag files here to upload</DropTitle>
          <DropHint>Alternatively, you can select file by</DropHint>
          <LinkButton type="button" onClick={openFilePicker}>
            Clicking here
          </LinkButton>
        </DropZone>
      ) : (
        <CertificateSection>
          <CertificatePreviewArea>
            <CanvasWrapper {...dragHandlers}>
              <CertificatePreview
                backgroundImage={previewSrc}
                certificateType={typeOfCertificate}
                alignment={selectedAlignment}
              />
              <ChangeBackgroundOverlay $dragging={dragging}>
                <ChangeBackgroundCard>
                  <ChangeBackgroundIcon>
                    <EditOutlined />
                  </ChangeBackgroundIcon>
                  <ChangeBackgroundText>To change the background</ChangeBackgroundText>
                  <LinkButton type="button" onClick={openFilePicker}>
                    Clicking here
                  </LinkButton>
                </ChangeBackgroundCard>
              </ChangeBackgroundOverlay>
            </CanvasWrapper>
          </CertificatePreviewArea>

          <AlignmentPanel>
            <AlignmentTitle>Text Alignment</AlignmentTitle>
            <StyledRadioGroup
              value={selectedAlignment}
              onChange={(event) => setSelectedAlignment(event.target.value)}
            >
              {ALIGNMENT_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </StyledRadioGroup>
          </AlignmentPanel>
        </CertificateSection>
      )}

      <FooterBar justify="end">
        <Col>
          <CancelButton onClick={onCancel || goBackToCertificates} disabled={submitting}>
            Cancel
          </CancelButton>
          <AddButton disabled={!canSubmit} loading={submitting} onClick={handleSubmit}>
            {isEditMode ? "Save" : "Add"}
          </AddButton>
        </Col>
      </FooterBar>

      <Modal open={successVisible} footer={null} centered closable={false} width={198}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            textAlign: "center",
          }}
        >
          <Tick style={{ width: "34px", height: "34px" }} />
          <p style={{ fontSize: "16px", fontWeight: 400, margin: "8px 0 0" }}>
            {isEditMode ? "Saved" : "Added"}
          </p>
        </div>
      </Modal>
    </PageWrapper>
  );
}
